/**
 * Gerenciador central de "um overlay ativo por vez".
 *
 * PROBLEMA REAL QUE ISSO RESOLVE (confirmado por cálculo, não suposição,
 * durante a auditoria de responsividade): newsPanel (bottom-left) e
 * reconPanel (bottom-right) eram ambos `position: absolute` com largura
 * própria e só um `max-width: calc(100vw - 32px)` de proteção — numa
 * tela de 375px de largura (iPhone SE), isso resultava em 320px de
 * sobreposição horizontal entre os dois painéis.
 *
 * EXTENSÃO (correção de bug crítico, não auditoria): popups de entidade
 * (câmera, voo, etc., abertos via clique num marker do mapa) NUNCA
 * foram integrados a este gerenciador — cada um dos dois sistemas
 * (bottom sheets `news`/`recon` vs. popup de entidade) vivia isolado,
 * então "abrir popup de câmera e depois tocar um chip de camada"
 * literalmente empilhava os dois, exatamente como reportado. A correção
 * trata o popup de entidade como um terceiro participante da mesma
 * pilha de exclusividade, com uma diferença importante: os bottom
 * sheets `news`/`recon` só competem entre si NO MOBILE (a tela tablet
 * tablet já tem espaço de sobra pros dois coexistirem, confirmado por
 * cálculo na auditoria anterior) — mas um popup sobre o MAPA competindo
 * visualmente com um painel HUD é sobreposição real EM QUALQUER
 * tamanho de tela, então a exclusividade envolvendo o popup de entidade
 * roda sempre, não só abaixo do breakpoint mobile.
 */

export type PanelId = 'news' | 'recon';

interface RegisteredPanel {
  element: HTMLElement;
  isOpenByDefault: boolean;
}

const MOBILE_BREAKPOINT_PX = 768; // precisa bater com global.css (max-width: 767px)

const registry = new Map<PanelId, RegisteredPanel>();

/** Callback que fecha o popup de entidade atualmente aberto, se
 *  houver. Registrado por quem gerencia o popup (ver main.ts) — este
 *  módulo não sabe construir/destruir popups do MapLibre diretamente,
 *  só precisa poder pedir pra fechar o que estiver aberto. */
let closeEntityPopupCallback: (() => void) | null = null;

export function registerEntityPopupCloser(closeFn: () => void): void {
  closeEntityPopupCallback = closeFn;
}

/** Chamado sempre que QUALQUER painel HUD (news, recon, ou um chip de
 *  camada) é ativado — garante que um popup de entidade aberto no mapa
 *  feche antes, em qualquer breakpoint. Exportado publicamente porque
 *  layerChips.ts precisa chamar isso diretamente quando um chip de
 *  CAMADA DE MAPA (não painel) é tocado — esse caso não passa por
 *  openPanel/closePanel, que só lidam com news/recon. */
export function closeEntityPopupIfOpen(): void {
  closeEntityPopupCallback?.();
}

function isMobileViewport(): boolean {
  return window.innerWidth < MOBILE_BREAKPOINT_PX;
}

/** Registra um painel na pilha. `isOpenByDefault` decide o estado
 *  inicial no tablet (sempre aberto) vs. mobile (fechado, exceto se
 *  explicitamente aberto por interação do usuário). */
export function registerPanel(id: PanelId, element: HTMLElement, isOpenByDefault = false): void {
  registry.set(id, { element, isOpenByDefault });

  if (isMobileViewport() && !isOpenByDefault) {
    element.style.display = 'none';
  }
}

/** Abre um painel, fechando todos os outros registrados E qualquer
 *  popup de entidade aberto no mapa (isso último acontece sempre,
 *  independente de breakpoint — ver nota de topo do arquivo). A
 *  exclusividade ENTRE os bottom sheets `news`/`recon` continua
 *  mobile-only. */
export function openPanel(id: PanelId): void {
  closeEntityPopupIfOpen();

  if (!isMobileViewport()) return; // tablet: news/recon sempre coexistem entre si

  for (const [otherId, panel] of registry) {
    panel.element.style.display = otherId === id ? '' : 'none';
  }
}

export function closePanel(id: PanelId): void {
  const panel = registry.get(id);
  if (panel) panel.element.style.display = 'none';
}

export function isPanelOpen(id: PanelId): boolean {
  const panel = registry.get(id);
  if (!panel) return false;
  return panel.element.style.display !== 'none';
}

/**
 * Chamado antes de abrir um popup de entidade no mapa (ver main.ts) —
 * fecha qualquer painel HUD que esteja competindo visualmente. No
 * mobile, isso significa fechar o bottom sheet aberto (se houver); no
 * tablet, onde news/recon ficam sempre visíveis nos cantos,
 * não há painel bottom sheet pra fechar, então esta chamada é
 * efetivamente no-op nesses breakpoints — o popup simplesmente abre
 * por cima do mapa, que é onde os painéis de canto não competem
 * espacialmente com ele.
 */
export function closeAllPanelsForEntityPopup(): void {
  if (!isMobileViewport()) return;

  for (const panel of registry.values()) {
    panel.element.style.display = 'none';
  }
}

/** Reavalia visibilidade ao cruzar o breakpoint (ex: rotação de
 *  ou redimensionamento de janela). Painéis voltam
 *  a ficar todos visíveis ao cruzar para tablet. */
export function handleViewportChange(): void {
  if (!isMobileViewport()) {
    for (const panel of registry.values()) {
      panel.element.style.display = '';
    }
  }
}

window.addEventListener('resize', handleViewportChange);
